import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";

type DataTablePaginationProps = {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  selectedItems?: number;
  /**
   * Total row count across all pages. When provided, the footer shows the
   * current item range ("1-10 of 14") per the design, taking priority over
   * the older "N rows selected" text.
   */
  totalItems?: number;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  showSelectedCount?: boolean;
};

export const DataTablePagination = ({
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  selectedItems = 0,
  totalItems,
  pageSizeOptions = [10, 20, 50, 100],
  showPageSizeSelector = true,
  showSelectedCount = true,
}: DataTablePaginationProps) => {
  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="flex items-center justify-between rounded-b-2xl border-t border-foreground-muted/20 bg-background-white px-8 py-4">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center space-x-6">
          {showPageSizeSelector && (
            <div className="flex shrink-0 items-center space-x-2">
              <span className="text-sm text-gray-700">Show</span>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => onPageSizeChange(Number(value))}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-700">per page</span>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={
                    currentPage === 1
                      ? undefined
                      : () => onPageChange(Math.max(1, currentPage - 1))
                  }
                  className={
                    currentPage === 1 ? "cursor-not-allowed opacity-50" : ""
                  }
                />
              </PaginationItem>

              {generatePageNumbers().map((page, index) => (
                <PaginationItem key={index}>
                  {page === "ellipsis" ? (
                    <PaginationEllipsis />
                  ) : (
                    <PaginationLink
                      onClick={() => onPageChange(page as number)}
                      isActive={currentPage === page}
                    >
                      {page}
                    </PaginationLink>
                  )}
                </PaginationItem>
              ))}

              <PaginationItem>
                <PaginationNext
                  onClick={
                    currentPage === totalPages
                      ? undefined
                      : () => onPageChange(Math.min(totalPages, currentPage + 1))
                  }
                  className={
                    currentPage === totalPages ? "cursor-not-allowed opacity-50" : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}

        {totalItems !== undefined ? (
          <div className="shrink-0">
            <span className="text-sm text-foreground-muted">
              {totalItems === 0
                ? "0 of 0"
                : `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalItems)} of ${totalItems}`}
            </span>
          </div>
        ) : (
          showSelectedCount && (
            <div className="shrink-0">
              <span className="text-sm text-foreground-muted">
                {selectedItems} row
                {selectedItems === 1 ? "" : "s"} selected
              </span>
            </div>
          )
        )}
      </div>
    </div>
  );
};
